<script setup>
import { computed, ref, defineEmits, watch } from 'vue';
import FilterGroup from '@/js/core/components/parts/calendar/FilterGroup.vue';
import { formatMonthLabel, monthToRange } from '@/js/core/utils/month-range.js';
import { calendarFilterGroups } from '@/js/core/utils/calendar-filter-groups.js';
import { allSelectionForGroup, removeFilterValue } from '@/js/core/utils/calendar-filter-state';
import { calendarFilterChips } from '@/js/core/utils/calendar-filter-chips';
import {
  academicYearMonthGroups,
  academicYearStartForDate,
  academicYearStartForMonthKey,
  monthToAcademicYearRange,
  sortDateRanges,
  yearForAcademicMonth,
} from '@/js/core/utils/academic-year.js';
import { useProfileStore } from '@/js/core/stores/profile-store.js';

const props = defineProps({
  yearsAndMonthsVisible: {
    type: Boolean,
    default: false,
  },
  monthNavigationVisible: {
    type: Boolean,
    default: true,
  },
  allowMultipleMonthSelection: {
    type: Boolean,
    default: false,
  },
  viewTypeVisible: {
    type: Boolean,
    default: true,
  },
  academicYearLinkVisible: {
    type: Boolean,
    default: true,
  },
  academicYears: {
    type: Array,
    default: () => [],
  },
  selectedMonths: {
    type: Array,
    required: true,
  },
  additionalClasses: {
    type: Array,
    default: () => [],
  },
  filterGroups: {
    type: Array,
    default: () => calendarFilterGroups,
  },
  idPrefix: {
    type: String,
    default: 'calendar-filters',
  },
  filterTypeVisible: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['months-selection-changed', 'year-changed']);

const MONTH_GROUPS = academicYearMonthGroups();

const profileStore = useProfileStore();

const openGroupId = ref(null);

// all dropdown groups start with 'all' selected
const selectedFilters = defineModel('filters', {
  type: Object,
  default: () => ({}),
});

const selectedMonths = ref(props.selectedMonths);
const availableAcademicYears = computed(() =>
  props.academicYears.length ? props.academicYears : [academicYearStartForDate()]
);
const activeFilterGroups = computed(() => props.filterGroups);
const monthDropdownId = computed(() => `${props.idPrefix}-month-dropdown`);
const monthPanelExpanded = ref(props.yearsAndMonthsVisible);

const toggleMonthPanel = () => {
  monthPanelExpanded.value = !monthPanelExpanded.value;
};

watch(
  () => props.yearsAndMonthsVisible,
  visible => {
    monthPanelExpanded.value = visible;
  }
);
const formattedSelectedMonth = computed(() =>
  formatMonthLabel(selectedMonths.value[0].start_after, false)
);

const selectedStartYear = ref(academicYearStartForMonthKey(selectedMonths.value[0].start_after));

// Multi-month selection spans a whole academic year, so a bare month name is
// ambiguous across the year boundary. Single-month selection already shows the
// year in the dropdown button, so its labels stay bare.
const monthGroups = computed(() => {
  if (!props.allowMultipleMonthSelection) {
    return MONTH_GROUPS;
  }

  return MONTH_GROUPS.map(group => ({
    ...group,
    months: group.months.map(month => ({
      ...month,
      label: `${month.label} ${yearForAcademicMonth(selectedStartYear.value, month.id)}`,
    })),
  }));
});

watch(
  () => props.selectedMonths,
  months => {
    selectedMonths.value = months;
    selectedStartYear.value = academicYearStartForMonthKey(months[0].start_after);
  }
);

const selectYearRange = startYear => {
  selectedStartYear.value = startYear;

  // Create the new set of months based on the selected year
  selectedMonths.value = selectedMonths.value.map(monthAndYear => {
    const month = monthAndYear.start_after.split('-')[1];

    return monthToAcademicYearRange(selectedStartYear.value, month);
  });

  emit('year-changed', startYear);
  emit('months-selection-changed', selectedMonths.value);
};

const isMonthActive = monthNum => {
  // selectedMonth is "YYYY-MM", so we grab the "MM" part
  return selectedMonths.value.find(
    selectedMonth => selectedMonth.start_after.split('-')[1] === monthNum
  );
};

const isGroupActive = group => {
  return group.months.every(month => isMonthActive(month.id));
};

const toggleGroup = group => {
  const allSelected = isGroupActive(group);

  group.months.forEach(month => {
    const active = isMonthActive(month.id);
    if (allSelected) {
      // If all are selected, we want to deselect all (unless it's the last one overall, handled by selectMonth)
      if (active) {
        selectMonth(month.id);
      }
    } else {
      // If not all are selected, we want to select the ones that aren't active
      if (!active) {
        selectMonth(month.id);
      }
    }
  });
};

const handleMonthChangeEvent = () => {
  emit('months-selection-changed', selectedMonths.value);
};

const selectMonth = month => {
  const range = monthToAcademicYearRange(selectedStartYear.value, month);

  // Handle multiple selection
  if (props.allowMultipleMonthSelection) {
    // Check if this month is currently selected
    const currentlySelectedIdx = selectedMonths.value.findIndex(
      selectedYearAndMonth => selectedYearAndMonth.start_after === range.start_after
    );

    if (currentlySelectedIdx !== -1) {
      // Deselect this month
      selectedMonths.value.splice(currentlySelectedIdx, 1);
    } else {
      // Select this month
      selectedMonths.value.push(range);
    }
  }

  // Handle single selection
  else {
    selectedMonths.value = [range];

    monthPanelExpanded.value = false;
  }

  selectedMonths.value = sortDateRanges(selectedMonths.value);

  handleMonthChangeEvent();
};

// Step one month either direction from the current selection, handling year jumps.
const adjacentMonth = direction => {
  const [year, month] = selectedMonths.value[0].start_after.split('-').map(Number);
  const date = new Date(year, month - 1, 15);

  date.setMonth(date.getMonth() + (direction === 'prev' ? -1 : 1));

  return {
    year: date.getFullYear(),
    month: String(date.getMonth() + 1).padStart(2, '0'),
  };
};

const changeMonth = direction => {
  const { year: newYear, month: newMonth } = adjacentMonth(direction);

  selectedStartYear.value = academicYearStartForMonthKey(`${newYear}-${newMonth}`);

  selectedMonths.value = [monthToRange(newYear, newMonth)];

  handleMonthChangeEvent();
};

// Screen-reader labels naming the month each arrow navigates to.
const prevMonthLabel = computed(() => {
  const { year, month } = adjacentMonth('prev');
  return formatMonthLabel(`${year}-${month}`, true);
});
const nextMonthLabel = computed(() => {
  const { year, month } = adjacentMonth('next');
  return formatMonthLabel(`${year}-${month}`, true);
});

const selectedFilterChips = computed(() =>
  calendarFilterChips(activeFilterGroups.value, selectedFilters.value)
);

const removeFilter = (groupId, itemId, field = null) => {
  selectedFilters.value[groupId] = removeFilterValue(
    selectedFilters.value[groupId],
    itemId,
    field,
    groupId
  );
};

const clearAllFilters = () => {
  Object.keys(selectedFilters.value).forEach(key => {
    selectedFilters.value[key] = allSelectionForGroup(key);
  });
};

// Logic for changing between each calendar view
const activeView = defineModel('view', {
  type: String,
  default: 'list',
});

const switchActiveView = type => {
  activeView.value = type;
  profileStore.savePreferences({ calendar_last_view_mode: type });
};

const toggleFilterGroup = id => {
  // If the same group is clicked, close it. Otherwise, set the new ID.
  openGroupId.value = openGroupId.value === id ? null : id;
};
</script>

<template>
  <div
    :class="[
      'calendar-page-filters-section',
      { 'allows-multiple-months-selection': props.allowMultipleMonthSelection },
      ...additionalClasses,
    ]"
  >
    <div class="calendar-page-filters">
      <div class="calendar-page-filter-month">
        <ul v-if="monthNavigationVisible">
          <li>
            <button
              type="button"
              :aria-label="`Load ${prevMonthLabel}`"
              @click="changeMonth('prev')"
            >
              <svg
                data-dir="left"
                viewBox="0 0 1001.79 901.97"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                focusable="false"
              >
                <line
                  fill="none"
                  stroke-linecap="round"
                  vector-effect="non-scaling-stroke"
                  x1="0.7"
                  x2="999.79"
                  y1="450.8"
                  y2="450.8"
                ></line>
                <polyline
                  fill="none"
                  points="461.58 0.99 1000.7 450.8 461.58 900.99"
                  stroke-linecap="round"
                  vector-effect="non-scaling-stroke"
                ></polyline>
              </svg>
            </button>
          </li>
          <li>
            <button
              type="button"
              :aria-label="`Load ${nextMonthLabel}`"
              @click="changeMonth('next')"
            >
              <svg
                data-dir="right"
                viewBox="0 0 1001.79 901.97"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                focusable="false"
              >
                <line
                  fill="none"
                  stroke-linecap="round"
                  vector-effect="non-scaling-stroke"
                  x1="0.7"
                  x2="999.79"
                  y1="450.8"
                  y2="450.8"
                ></line>
                <polyline
                  fill="none"
                  points="461.58 0.99 1000.7 450.8 461.58 900.99"
                  stroke-linecap="round"
                  vector-effect="non-scaling-stroke"
                ></polyline>
              </svg>
            </button>
          </li>
        </ul>
        <button
          v-if="monthNavigationVisible && !allowMultipleMonthSelection"
          type="button"
          class="calendar-filter-month-dropdown-button"
          :aria-expanded="monthPanelExpanded"
          :aria-controls="monthDropdownId"
          @click="toggleMonthPanel"
        >
          <div class="calendar-filter-current-month">
            <span>{{ formattedSelectedMonth }}</span>
          </div>
          <div class="calendar-filter-current-month-icon">
            <svg viewBox="0 0 501.62 501.62" xmlns="http://www.w3.org/2000/svg">
              <polygon
                points="250.81 500.5 500.81 0.5 0.81 0.5 250.81 500.5"
                vector-effect="non-scaling-stroke"
              ></polygon>
            </svg>
          </div>
        </button>
        <div
          :id="monthDropdownId"
          class="calendar-filter-current-month-dropdown"
          :class="{ 'is-open': monthPanelExpanded }"
        >
          <div class="calendar-filter-month-dropdown-wrapper">
            <fieldset class="calendar-dropdown-academic-year-column">
              <legend>Academic Year</legend>
              <template v-for="year in availableAcademicYears" :key="year">
                <div class="field-wrapper">
                  <input
                    :id="`${props.idPrefix}-filter-year-${year}`"
                    type="radio"
                    :checked="selectedStartYear === year"
                    @change="selectYearRange(year)"
                  />
                  <label :for="`${props.idPrefix}-filter-year-${year}`"
                    >{{ year }}/{{ year + 1 }}</label
                  >
                </div>
              </template>
            </fieldset>
            <div class="calendar-dropdown-semester-columns">
              <fieldset class="calendar-dropdown-semester-year">
                <legend>Month Selection</legend>
                <fieldset
                  v-for="group in monthGroups"
                  :key="group.id"
                  class="calendar-dropdown-semester-items"
                >
                  <legend>{{ group.name }}</legend>

                  <template v-if="allowMultipleMonthSelection && group.months.length > 0">
                    <div class="field-wrapper filter-months-select-all-wrapper">
                      <input
                        :id="`${props.idPrefix}-filter-${group.id}-all`"
                        :checked="isGroupActive(group)"
                        type="checkbox"
                        @change="toggleGroup(group)"
                      />
                      <label
                        :for="`${props.idPrefix}-filter-${group.id}-all`"
                        :title="`Select all ${group.name.toLowerCase()} months`"
                      >
                        <span class="visually-hidden">Select all </span>
                        <span>{{ group.name }}</span>
                      </label>
                    </div>
                  </template>

                  <template v-for="month in group.months" :key="month.id">
                    <div class="field-wrapper">
                      <input
                        :id="`${props.idPrefix}-filter-month-${month.id}`"
                        :type="allowMultipleMonthSelection ? 'checkbox' : 'radio'"
                        :checked="isMonthActive(month.id)"
                        @change="selectMonth(month.id)"
                      />
                      <label :for="`${props.idPrefix}-filter-month-${month.id}`">{{
                        month.label
                      }}</label>
                    </div>
                  </template>
                </fieldset>
              </fieldset>
            </div>
            <div v-if="academicYearLinkVisible" class="calendar-dropdown-fullyear-button">
              <RouterLink
                :to="{ name: 'academic-calendar' }"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View Full Academic Year (opens in a new window)"
              >
                View Full Academic Year
              </RouterLink>
              <span>
                <svg
                  data-dir="up_right"
                  viewBox="0 0 1001.79 901.97"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <line
                    fill="none"
                    stroke-linecap="round"
                    vector-effect="non-scaling-stroke"
                    x1="0.7"
                    x2="999.79"
                    y1="450.8"
                    y2="450.8"
                  ></line>
                  <polyline
                    fill="none"
                    points="461.58 0.99 1000.7 450.8 461.58 900.99"
                    stroke-linecap="round"
                    vector-effect="non-scaling-stroke"
                  ></polyline>
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="viewTypeVisible" class="calendar-page-toggle-view">
        <ul>
          <li class="calendar-page-toggle-view-tab" :class="{ 'is-active': activeView === 'list' }">
            <button
              type="button"
              :aria-current="activeView === 'list' ? 'true' : undefined"
              @click="switchActiveView('list')"
            >
              List
            </button>
          </li>
          <li
            class="calendar-page-toggle-view-tab"
            :class="{ 'is-active': activeView === 'calendar' }"
          >
            <button
              type="button"
              :aria-current="activeView === 'calendar' ? 'true' : undefined"
              @click="switchActiveView('calendar')"
            >
              Calendar
            </button>
          </li>
          <li
            class="calendar-page-toggle-view-tab"
            :class="{ 'is-active': activeView === 'categories' }"
          >
            <button
              type="button"
              :aria-current="activeView === 'categories' ? 'true' : undefined"
              @click="switchActiveView('categories')"
            >
              Categories
            </button>
          </li>
        </ul>
      </div>
      <div v-if="filterTypeVisible" class="calendar-page-filter-type">
        <div class="calendar-type-filters">
          <FilterGroup
            v-for="group in activeFilterGroups"
            :key="group.id"
            v-bind="group"
            v-model="selectedFilters[group.id]"
            :is-open="openGroupId === group.id"
            @toggle="toggleFilterGroup(group.id)"
          />
        </div>
        <div
          v-if="selectedFilterChips.length"
          class="calendar-type-filter__selected-list-container"
        >
          <div class="calendar-type-filter__selected-list">
            <div class="calendar-type-filter__listing-label">Listing:</div>
            <div
              v-for="item in selectedFilterChips"
              :key="item.key"
              class="calendar-type-filter__selected-label"
            >
              {{ item.label }}
              <button
                type="button"
                class="calendar-type-filter__remove"
                :aria-label="'Remove ' + item.label + ' filter'"
                @click="removeFilter(item.groupId, item.id, item.field)"
              >
                <svg viewBox="0 0 100 100" aria-hidden="true">
                  <line x1="0" y1="0" x2="100" y2="100" stroke="currentColor" stroke-width="10" />
                  <line x1="100" y1="0" x2="0" y2="100" stroke="currentColor" stroke-width="10" />
                </svg>
              </button>
            </div>
          </div>

          <button
            type="button"
            class="calendar-type-filter__option--clear"
            @click="clearAllFilters"
          >
            <svg viewBox="0 0 100 100">
              <line x1="0" y1="0" x2="100" y2="100" stroke="currentColor" stroke-width="10" />
              <line x1="100" y1="0" x2="0" y2="100" stroke="currentColor" stroke-width="10" />
            </svg>
            Clear Filters
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
